import { useCallback, useMemo, useState } from "react";
import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd";

import { DeleteRowModal } from "./DeleteRowModal";
import { ZeroRowModal } from "./ZeroRowModal";
import { QuoteTableHeader } from "./QuoteTableHeader";
import { QuoteTableRow } from "./QuoteTableRow";
import { QuoteTableTotalsRow } from "./QuoteTableTotalsRow";
import { QuoteSummaryTotals } from "./QuoteSummaryTotals";
import type { SummaryTotalRow } from "./QuoteSummaryTotals";
import { formatCurrency } from "./utils";
import type { QuoteTableColumn, QuoteTableMode, ColumnHeader } from "./quote-table.types";
import type { RfxQuoteItem } from "../../Submission/types/quote.type";
import { SupplierRowsSection } from "../../Submission/components/QuoteTab/SupplierRowsSection";
import type { SupplierRowsSectionProps, SupplierRowsSectionHandle } from "../../Submission/components/QuoteTab/SupplierRowsSection";

export interface TotalRow {
	id: string;
	name: string;
	color: string;
	value: number;
}

export interface UnitOption {
	label: string;
	value: number;
}

export interface DynamicQuoteTableProps {
	allValues?: Record<string, Record<string, number | string>>;
	columns: QuoteTableColumn[];
	columnValues?: Record<number, Record<number, string>>;
	dirtyRowValues?: Record<number, { unit_id?: number | null; name?: string; }>;
	disabled: boolean;
	editValues?: Record<number, Record<number, { quantity: string; rate: string }>>;
	includeTotalsRow?: boolean;
	items: RfxQuoteItem[];
	mode?: QuoteTableMode;
	rowErrors?: Record<number, { unit_id?: boolean; cells?: Record<number, boolean>; name?: boolean; }>;
	supplierColumnTotals?: Record<number, number>;
	supplierRowsProps?: Omit<SupplierRowsSectionProps, 'disabled' | 'isClientView'>;
	supplierRowsRef?: React.Ref<SupplierRowsSectionHandle>;
	totals?: TotalRow[];
	unitOptions?: UnitOption[];
	onCellValueChange?: (itemId: number, columnId: number, field: string, value: string) => void;
	onFormikFieldChange?: (itemId: number, field: string, value: number | string) => void;
	onRowDelete?: (rowId: number) => void;
	onRowReorder?: (rowIds: number[]) => void;
	onRowUpdate?: (rowId: number, payload: { unit_id?: number | null; name?: string; }) => void;
}

const DynamicQuoteTable: React.FC<DynamicQuoteTableProps> = ({
	allValues = {},
	columns,
	disabled,
	items,
	mode,
	onRowDelete,
	onRowReorder,
	onRowUpdate,
	unitOptions = [],
	dirtyRowValues = {},
	rowErrors = {},
	editValues = {},
	columnValues = {},
	includeTotalsRow = false,
	onCellValueChange,
	onFormikFieldChange,
	supplierColumnTotals = {},
	supplierRowsProps,
	supplierRowsRef,
	totals = [],
}) => {
	const isProcurerView = mode === "edit";
	const isClientView = mode === "view";
	const showActionsColumn = isProcurerView && !disabled && !!onRowDelete;

	const primaryDescriptionColId = useMemo(() => {
		const sorted = [...columns].sort((a, b) => a.sortOrder - b.sortOrder);
		return sorted.find((c) => c.columnType === "description")?.id ?? null;
	}, [columns]);

	const duplicateColumnNames = useMemo(() => {
		const labelCounts = new Map<string, number>();
		for (const col of columns) {
			const lower = col.label.toLowerCase();
			labelCounts.set(lower, (labelCounts.get(lower) || 0) + 1);
		}
		const dupes = new Set<string>();
		for (const [label, count] of labelCounts) {
			if (count > 1) {
				dupes.add(label);
			}
		}
		return dupes;
	}, [columns]);

	const isRespondMode = mode === "respond";
	const showZeroButton = isRespondMode && !disabled;

	const headers = useMemo((): ColumnHeader[] => {
		const result: ColumnHeader[] = [
			{ key: "idx", label: "#", width: "3%" },
		];
		const sortedCols = [...columns].sort((a, b) => a.sortOrder - b.sortOrder);
		for (const col of sortedCols) {
			result.push({
				key: `col-${col.id}`,
				label: col.label,
				width: col.columnType === "description" ? "25%" : "12%",
				columnId: col.id,
				columnType: col.columnType,
				isDefault: col.isDefault,
				isFirstOfGroup: true,
			});
		}
		return result;
	}, [columns]);

	// Summary totals — convert TotalRow (numeric) to SummaryTotalRow (pre-formatted)
	const { summaryTotals, summarySpacerColSpan, summaryTrailingColSpan } = useMemo(() => {
		const hasTotalCol = columns.some((c) => c.columnType === "total");
		if (!hasTotalCol || totals.length === 0) {
			return { summaryTotals: [] as SummaryTotalRow[], summarySpacerColSpan: 1, summaryTrailingColSpan: 0 };
		}
		const costIdx = headers.findIndex((h) => h.columnType === "total");
		const costColIndex = costIdx >= 0 ? costIdx : headers.length - 1;
		const labelColIndex = costColIndex - 1;
		const spacer = Math.max(labelColIndex, 1) + (showZeroButton ? 1 : 0);
		const trailing = headers.length - costColIndex - 1 + (showActionsColumn ? 1 : 0);

		const mapped: SummaryTotalRow[] = totals.map((row) => ({
			id: row.id,
			label: row.name,
			color: row.color,
			displayValue: formatCurrency(row.value),
		}));

		return { summaryTotals: mapped, summarySpacerColSpan: spacer, summaryTrailingColSpan: trailing };
	}, [columns, headers, totals, showZeroButton, showActionsColumn]);

	// Columns that get zeroed
	const numericColIds = useMemo(() => {
		return columns
			.filter((c) => c.columnType === "response_value" || c.columnType === "response_quantity" || c.columnType === "response_number")
			.map((c) => ({ id: c.id, type: c.columnType }));
	}, [columns]);

	// --- Row management ---
	const [pendingDeleteRowId, setPendingDeleteRowId] = useState<number | null>(null);
	const [pendingZeroRowId, setPendingZeroRowId] = useState<number | null>(null);

	const handleConfirmDeleteRow = useCallback(() => {
		if (pendingDeleteRowId && onRowDelete) {
			onRowDelete(pendingDeleteRowId);
		}
		setPendingDeleteRowId(null);
	}, [pendingDeleteRowId, onRowDelete]);

	const zeroRow = useCallback((rowId: number) => {
		if (!onFormikFieldChange) {
			return;
		}
		for (const col of numericColIds) {
			if (col.type === "response_quantity") {
				onFormikFieldChange(rowId, "quantity", 0);
			} else if (col.type === "response_value") {
				onFormikFieldChange(rowId, `rate_${col.id}`, 0);
			} else if (col.type === "response_number") {
				onFormikFieldChange(rowId, `number_${col.id}`, 0);
			}
		}
	}, [numericColIds, onFormikFieldChange]);

	const handleZeroClick = useCallback((rowId: number) => {
		// Check if row has any existing values
		const formikValues = allValues[`quote-${rowId}`];
		const hasValues = numericColIds.some((col) => {
			let val: number | string | undefined;
			if (col.type === "response_quantity") {
				val = formikValues?.["quantity"];
			} else if (col.type === "response_value") {
				val = formikValues?.[`rate_${col.id}`];
			} else if (col.type === "response_number") {
				val = formikValues?.[`number_${col.id}`];
			}
			return val !== undefined && val !== null && val !== "" && val !== 0 && val !== "0";
		});

		if (hasValues) {
			setPendingZeroRowId(rowId);
		} else {
			zeroRow(rowId);
		}
	}, [allValues, numericColIds, zeroRow]);

	const handleConfirmZeroRow = useCallback(() => {
		if (pendingZeroRowId !== null) {
			zeroRow(pendingZeroRowId);
		}
		setPendingZeroRowId(null);
	}, [pendingZeroRowId, zeroRow]);

	const handleRowDragEnd = useCallback(
		(result: DropResult) => {
			if (!result.destination || !onRowReorder) {
				return;
			}
			const startIndex = result.source.index;
			const endIndex = result.destination.index;
			if (startIndex === endIndex) {
				return;
			}
			const ids = items.map((i) => i.id);
			const [movedId] = ids.splice(startIndex, 1);
			ids.splice(endIndex, 0, movedId);
			onRowReorder(ids);
		},
		[items, onRowReorder]
	);

	// --- Auto-calculated column totals ---
	const columnTotals = useMemo(() => {
		if (!includeTotalsRow) {
			return null;
		}
		const totalsMap: Record<number, number> = {};
		const responseValueCols = columns.filter((c) => c.columnType === "response_value");
		for (const col of responseValueCols) {
			let sum = 0;
			for (const item of items) {
				const formikVal = allValues[`quote-${item.id}`]?.[`rate_${col.id}`];
				const val = Number(formikVal ?? columnValues[item.id]?.[col.id] ?? editValues[item.id]?.[col.id]?.rate ?? 0);
				if (!isNaN(val)) {
					sum += val;
				}
			}
			totalsMap[col.id] = sum + (supplierColumnTotals[col.id] ?? 0);
		}
		return totalsMap;
	}, [includeTotalsRow, columns, items, allValues, columnValues, editValues, supplierColumnTotals]);

	// --- Cell props shared across all rows ---
	const cellProps = useMemo(() => ({
		allValues,
		columns,
		columnValues,
		disabled,
		dirtyRowValues,
		editValues,
		mode: mode ?? ("respond" as QuoteTableMode),
		onCellValueChange,
		onFormikFieldChange,
		onRowUpdate,
		rowErrors,
		unitOptions,
	}), [allValues, columns, columnValues, disabled, dirtyRowValues, editValues, mode, onCellValueChange, onFormikFieldChange, onRowUpdate, rowErrors, unitOptions]);

	return (
		<>
			<div
				className="overflow-x-auto"
				data-testid="dynamic-quote-table-wrapper"
			>
				<table
					className="w-full table-fixed rounded-xl border-b border-solid border-[#EAECF0] bg-white"
					style={{ minWidth: `${Math.max(800, headers.length * 100)}px` }}
					data-testid="dynamic-quote-table"
				>
					<caption className="sr-only">Quote table</caption>

					<QuoteTableHeader
						columns={columns}
						duplicateColumnNames={duplicateColumnNames}
						headers={headers}
						showActionsColumn={showActionsColumn}
						showZeroColumn={showZeroButton}
					/>

					<DragDropContext onDragEnd={handleRowDragEnd}>
						<Droppable droppableId="quote-table-rows" direction="vertical">
							{(droppableProvided) => (
								<tbody
									ref={droppableProvided.innerRef}
									{...droppableProvided.droppableProps}
								>
									{items.map((item, idx) => (
										<Draggable
											key={item.id}
											draggableId={`row-${item.id}`}
											index={idx}
											isDragDisabled={!isProcurerView || disabled || !onRowReorder}
										>
											{(draggableProvided) => (
												<tr
													ref={draggableProvided.innerRef}
													{...draggableProvided.draggableProps}
													className="border-b border-[#EAECF0]"
												>
													<QuoteTableRow
														cellProps={cellProps}
														columns={columns}
														dragHandleProps={draggableProvided.dragHandleProps ?? undefined}
														headers={headers}
														index={idx}
														item={item}
														onDeleteClick={setPendingDeleteRowId}
														onZeroClick={handleZeroClick}
														primaryDescriptionColId={primaryDescriptionColId}
														showDeleteAction={showActionsColumn}
														showDragHandle={isProcurerView && !disabled && !!onRowReorder}
														showZeroButton={showZeroButton}
													/>
												</tr>
											)}
										</Draggable>
									))}
									{droppableProvided.placeholder}
								</tbody>
							)}
						</Droppable>
					</DragDropContext>

					{supplierRowsProps && (
						<SupplierRowsSection
							ref={supplierRowsRef}
							disabled={disabled}
							isClientView={isClientView}
							showZeroColumn={showZeroButton}
							{...supplierRowsProps}
						/>
					)}

					{columnTotals && (
						<QuoteTableTotalsRow
							columns={columns}
							columnTotals={columnTotals}
							showActionsColumn={showActionsColumn}
							showZeroColumn={showZeroButton}
						/>
					)}

					{summaryTotals.length > 0 && (
						<tbody>
							<QuoteSummaryTotals
								spacerColSpan={summarySpacerColSpan}
								totals={summaryTotals}
								trailingColSpan={summaryTrailingColSpan}
							/>
						</tbody>
					)}
				</table>
			</div>

			<DeleteRowModal
				isOpen={pendingDeleteRowId !== null}
				onClose={() => setPendingDeleteRowId(null)}
				onConfirm={handleConfirmDeleteRow}
			/>

			<ZeroRowModal
				isOpen={pendingZeroRowId !== null}
				onClose={() => setPendingZeroRowId(null)}
				onConfirm={handleConfirmZeroRow}
			/>
		</>
	);
};

export { DynamicQuoteTable };
